// StoreKit purchases through RevenueCat, for the native iOS shell only.
//
// Every export here answers immediately and harmlessly on the web: isIOSNative()
// is false in every browser, including the installed PWA, so nothing below ever
// runs there and the RevenueCat SDK chunk is never requested. The SDK is reached
// only through loadNative(() => import(...)), which is a no-op off native.
//
// The linchpin of the whole entitlement design is one line in initPurchases():
//
//     Purchases.logIn({ appUserID: supabaseUserId })
//
// The RevenueCat app user id IS the Supabase auth uid. That is what lets
// server/revenuecat-webhook.js write user_id onto the subscriptions row for a
// Sign in with Apple "Hide My Email" account, whose relay address matches no
// row and no auth email anywhere in the existing Stripe data. Entitlements must
// never be keyed on email. If they were, every Hide My Email purchaser would pay
// and then be shown the paywall.

import { isIOSNative, loadNative } from '@/lib/platform';
import {
  ENTITLEMENT_ID,
  PRODUCT_ID,
  REVENUECAT_IOS_API_KEY,
  APPLE_MANAGE_SUBSCRIPTIONS_URL,
} from './config';
import { rememberEntitlement, readCachedEntitlement, forgetEntitlement } from './entitlementCache';
import {
  trackNativeCheckoutStarted,
  trackNativePurchaseSucceeded,
  trackNativePurchaseCancelled,
  trackNativePurchaseFailed,
  trackNativeRestore,
} from './analytics';

// RevenueCat error codes are strings in PURCHASES_ERROR_CODE. Mapped to
// translation keys rather than to English text, so every message below exists in
// all six locales. The numbers are quoted from the SDK enum; the names are there
// so this table stays readable if the numbering ever changes.
const ERROR_MESSAGE_KEYS = {
  '1': null,                                 // PURCHASE_CANCELLED_ERROR, handled as "cancelled", not an error
  '2': 'native.purchase.unavailable',        // STORE_PROBLEM_ERROR
  '3': 'native.purchase.notAllowed',         // PURCHASE_NOT_ALLOWED_ERROR (restrictions, Screen Time)
  '5': 'native.purchase.unavailable',        // PRODUCT_NOT_AVAILABLE_FOR_PURCHASE_ERROR
  '6': 'native.purchase.alreadyOwned',       // PRODUCT_ALREADY_PURCHASED_ERROR
  '10': 'native.purchase.network',           // NETWORK_ERROR
  '20': 'native.purchase.pending',           // PAYMENT_PENDING_ERROR (Ask to Buy waiting on a parent)
  '23': 'native.purchase.unavailable',       // CONFIGURATION_ERROR
};

let sdkPromise = null;
let currentInit = { userId: null, promise: null };
let listenerInstalled = false;

async function sdk() {
  if (!isIOSNative()) return null;
  if (!sdkPromise) {
    sdkPromise = loadNative(() => import('@revenuecat/purchases-capacitor'));
  }
  return sdkPromise;
}

function messageKeyForError(err) {
  const code = String(err?.code ?? '');
  if (Object.prototype.hasOwnProperty.call(ERROR_MESSAGE_KEYS, code)) {
    return ERROR_MESSAGE_KEYS[code];
  }
  return 'native.purchase.failed';
}

function isCancellation(err) {
  return err?.userCancelled === true || String(err?.code ?? '') === '1';
}

/**
 * Turn a RevenueCat CustomerInfo into the small shape the rest of the app cares
 * about, and remember it for the offline path.
 */
async function snapshotFrom(customerInfo, userId) {
  const info = customerInfo?.entitlements?.active?.[ENTITLEMENT_ID] || null;
  const snapshot = {
    active: !!info,
    expiresAtMs: Number.isFinite(info?.expirationDateMillis) ? info.expirationDateMillis : null,
    willRenew: info?.willRenew === true,
    productId: info?.productIdentifier || null,
    source: 'revenuecat',
  };
  await rememberEntitlement({ userId, active: snapshot.active, expiresAtMs: snapshot.expiresAtMs });
  return snapshot;
}

/**
 * Configure RevenueCat and bind it to this Supabase user.
 *
 * Idempotent and safe to call on every auth change: repeated calls for the same
 * uid share one promise, and a different uid triggers a logIn rather than a
 * second configure.
 *
 * @param {string} userId Supabase auth uid (user.id), never an email.
 */
export function initPurchases(userId) {
  if (!isIOSNative()) return Promise.resolve({ ok: false, reason: 'not_native' });
  // Never configure anonymously. An anonymous RevenueCat id would put the
  // purchase on a '$RCAnonymousID:...' user, and the webhook would have no
  // Supabase uid to file the row under.
  if (!userId) return Promise.resolve({ ok: false, reason: 'no_user' });
  if (currentInit.userId === userId && currentInit.promise) return currentInit.promise;

  const promise = doInit(userId);
  currentInit = { userId, promise };
  return promise;
}

async function doInit(userId) {
  const m = await sdk();
  if (!m?.Purchases) return { ok: false, reason: 'sdk_unavailable' };
  if (!REVENUECAT_IOS_API_KEY) {
    console.warn('[purchases] VITE_REVENUECAT_IOS_API_KEY is not set, StoreKit is disabled');
    return { ok: false, reason: 'no_api_key' };
  }

  const { Purchases } = m;
  try {
    if (import.meta.env.DEV && m.LOG_LEVEL?.DEBUG) {
      await Purchases.setLogLevel({ level: m.LOG_LEVEL.DEBUG });
    }

    let configured = false;
    try {
      configured = (await Purchases.isConfigured())?.isConfigured === true;
    } catch {
      configured = false;
    }

    if (!configured) {
      await Purchases.configure({ apiKey: REVENUECAT_IOS_API_KEY, appUserID: userId });
    } else {
      let current = null;
      try {
        current = (await Purchases.getAppUserID())?.appUserID || null;
      } catch {
        current = null;
      }
      if (current !== userId) {
        await Purchases.logIn({ appUserID: userId });
      }
    }

    // Keep the offline cache warm. StoreKit can hand RevenueCat a renewal while
    // the app is open, and this is the only push we get for it.
    if (!listenerInstalled) {
      listenerInstalled = true;
      try {
        await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
          void snapshotFrom(customerInfo, userId);
        });
      } catch (listenerErr) {
        listenerInstalled = false;
        console.warn('[purchases] customer info listener failed:', listenerErr?.message || listenerErr);
      }
    }

    return { ok: true, userId };
  } catch (err) {
    console.warn('[purchases] configure failed:', err?.message || err);
    return { ok: false, reason: 'configure_failed' };
  }
}

/**
 * The current StoreKit entitlement, or null.
 *
 * null means UNKNOWN, never "not subscribed": on the web, before configure, or
 * when RevenueCat is unreachable and there is no usable cache. Callers must
 * therefore only ever grant on `active === true` and must never revoke on null.
 *
 * @param {string|null} userId Supabase auth uid, used to validate the cache owner.
 * @returns {Promise<{active:boolean, expiresAtMs:number|null, willRenew:boolean, source:string}|null>}
 */
export async function getNativeEntitlement(userId = null) {
  if (!isIOSNative()) return null;
  const m = await sdk();
  if (!m?.Purchases) return null;

  try {
    const configured = (await m.Purchases.isConfigured())?.isConfigured === true;
    if (!configured) return await readCachedEntitlement(userId);
    const { customerInfo } = await m.Purchases.getCustomerInfo();
    return await snapshotFrom(customerInfo, userId);
  } catch (err) {
    console.warn('[purchases] getCustomerInfo failed, falling back to cache:', err?.message || err);
    return await readCachedEntitlement(userId);
  }
}

/**
 * Present the StoreKit purchase sheet for the single monthly product.
 *
 * Never throws. The paywall gets back a plain result object and a translation
 * key, so no English string is invented at the call site.
 *
 * @param {{ userId?: string|null, analytics?: object }} options
 * @returns {Promise<{ok:boolean, active:boolean, cancelled:boolean, messageKey:string|null, reason?:string}>}
 */
export async function purchaseMonthly(options = {}) {
  const { userId = null, analytics = {} } = options;
  if (!isIOSNative()) {
    return { ok: false, active: false, cancelled: false, messageKey: null, reason: 'not_native' };
  }

  const m = await sdk();
  if (!m?.Purchases) {
    return {
      ok: false,
      active: false,
      cancelled: false,
      messageKey: 'native.purchase.unavailable',
      reason: 'sdk_unavailable',
    };
  }
  const { Purchases } = m;

  // Bind to the account first. A purchase made before logIn lands on an
  // anonymous RevenueCat id and the webhook cannot file it.
  if (userId) await initPurchases(userId);

  // Resolve what to buy. The offering is the configured path; the direct product
  // lookup is the safety net for a misconfigured or not-yet-published offering,
  // which would otherwise show a working button that can never charge anyone.
  let pkg = null;
  let product = null;
  try {
    const offerings = await Purchases.getOfferings();
    const available = offerings?.current?.availablePackages || [];
    pkg =
      available.find((p) => p?.product?.identifier === PRODUCT_ID) ||
      available[0] ||
      null;
  } catch (err) {
    console.warn('[purchases] getOfferings failed:', err?.message || err);
  }
  if (!pkg) {
    try {
      const res = await Purchases.getProducts({ productIdentifiers: [PRODUCT_ID] });
      product = res?.products?.[0] || null;
    } catch (err) {
      console.warn('[purchases] getProducts failed:', err?.message || err);
    }
  }
  if (!pkg && !product) {
    return {
      ok: false,
      active: false,
      cancelled: false,
      messageKey: 'native.purchase.unavailable',
      reason: 'no_product',
    };
  }

  // The price shown to PostHog is the price StoreKit is about to charge, in the
  // user's own store currency, read off the same object the sheet renders from.
  const storeProduct = pkg?.product || product;
  trackNativeCheckoutStarted({
    price: storeProduct?.price ?? null,
    price_string: storeProduct?.priceString || undefined,
    currency: storeProduct?.currencyCode || undefined,
    product_id: storeProduct?.identifier || PRODUCT_ID,
    ...analytics,
  });

  try {
    const result = pkg
      ? await Purchases.purchasePackage({ aPackage: pkg })
      : await Purchases.purchaseStoreProduct({ product });
    const snapshot = await snapshotFrom(result?.customerInfo, userId);
    trackNativePurchaseSucceeded({
      product_id: result?.productIdentifier || storeProduct?.identifier || PRODUCT_ID,
      entitlement_active: snapshot.active,
    });
    return { ok: true, active: snapshot.active, cancelled: false, messageKey: null };
  } catch (err) {
    if (isCancellation(err)) {
      trackNativePurchaseCancelled({ product_id: storeProduct?.identifier || PRODUCT_ID });
      return { ok: false, active: false, cancelled: true, messageKey: null, reason: 'cancelled' };
    }
    const messageKey = messageKeyForError(err);
    console.warn('[purchases] purchase failed:', err?.code, err?.message || err);
    trackNativePurchaseFailed({
      product_id: storeProduct?.identifier || PRODUCT_ID,
      code: String(err?.code ?? 'unknown'),
    });
    // PAYMENT_PENDING is not a failure: Ask to Buy is waiting on a parent, and
    // the entitlement arrives later through the customer info listener.
    const pending = String(err?.code ?? '') === '20';
    return { ok: pending, active: false, cancelled: false, messageKey, reason: pending ? 'pending' : 'failed' };
  }
}

/**
 * Ask StoreKit for anything this Apple Account has already bought.
 *
 * The web paywall keeps its existing Stripe restore path afterwards: a person
 * who paid on the website and then installed the app has nothing in StoreKit to
 * restore, and must still get in.
 *
 * @returns {Promise<{ok:boolean, active:boolean, messageKey:string|null}>}
 */
export async function restoreNativePurchases(options = {}) {
  const { userId = null } = options;
  if (!isIOSNative()) return { ok: false, active: false, messageKey: null };

  const m = await sdk();
  if (!m?.Purchases) return { ok: false, active: false, messageKey: null };

  if (userId) await initPurchases(userId);

  try {
    const { customerInfo } = await m.Purchases.restorePurchases();
    const snapshot = await snapshotFrom(customerInfo, userId);
    trackNativeRestore({ restored: snapshot.active });
    return {
      ok: true,
      active: snapshot.active,
      // No message when nothing was found: the caller falls through to the
      // Stripe restore, and saying "nothing to restore" before that lookup has
      // even run would be wrong for every web payer.
      messageKey: snapshot.active ? 'native.restore.restored' : null,
    };
  } catch (err) {
    console.warn('[purchases] restore failed:', err?.code, err?.message || err);
    trackNativeRestore({ restored: false, code: String(err?.code ?? 'unknown') });
    return { ok: false, active: false, messageKey: null };
  }
}

/**
 * Send the user to Settings > Apple Account > Subscriptions.
 *
 * On iOS the Stripe billing portal must be unreachable: an Apple-billed
 * subscription cannot be cancelled, refunded or discounted by us, and pointing a
 * purchaser at our own billing page for a purchase Apple owns is both a broken
 * promise to the user and a Guideline 3.1.1 problem.
 */
export async function openManageSubscriptions() {
  if (!isIOSNative()) return { ok: false, reason: 'not_native' };

  let url = APPLE_MANAGE_SUBSCRIPTIONS_URL;
  try {
    const m = await sdk();
    if (m?.Purchases) {
      const { customerInfo } = await m.Purchases.getCustomerInfo();
      const managementUrl = customerInfo?.managementURL;
      // Only honour a non-http management URL. An https apps.apple.com URL would
      // navigate the webview and replace the app with a web page.
      if (typeof managementUrl === 'string' && managementUrl.startsWith('itms-')) {
        url = managementUrl;
      }
    }
  } catch {
    /* the documented deep link below always works as a fallback */
  }

  try {
    // A non-http scheme is handed to the system by the native bridge rather than
    // navigated inside the webview.
    window.location.href = url;
    return { ok: true };
  } catch (err) {
    console.warn('[purchases] could not open manage subscriptions:', err?.message || err);
    return { ok: false, messageKey: 'native.manage.failed' };
  }
}

/** Called on sign out so the next account on this device starts clean. */
export async function logOutPurchases() {
  await forgetEntitlement();
  currentInit = { userId: null, promise: null };
  if (!isIOSNative()) return;
  try {
    const m = await sdk();
    if (m?.Purchases) await m.Purchases.logOut();
  } catch (err) {
    // Logging out an already-anonymous user throws. Harmless.
    console.warn('[purchases] logOut:', err?.message || err);
  }
}

/**
 * True when a subscriptions row is billed by Apple, so the Stripe-only controls
 * (billing portal, cancel, retention discount) must not be offered for it.
 *
 * Branch on this, NOT on isNative(): a web payer holding a Stripe row and using
 * the iOS app still has a subscription that really is ours to manage, and taking
 * those controls away from them would be a regression. A NULL provider means
 * Stripe, which is why every legacy row keeps working untouched.
 */
export function isAppleBilledRow(row) {
  return String(row?.provider || '').toLowerCase() === 'apple';
}
